import {
  Component,
  ChangeDetectionStrategy,
  Injector,
  ViewChild,
  ElementRef,
  AfterViewInit,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { debounceTime, takeUntil, throttleTime } from 'rxjs/operators';
import { TuiDialogService, TuiScrollbar } from '@taiga-ui/core';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { NgxPermissionsService } from 'ngx-permissions';
import { BaseResizableComponent } from '@src/app/modules/base';
import { ListModeType } from '@src/app/modules/list-mode-switcher';
import { StorageKeys } from '@src/constants/storage';
import { PlacemarkType, PlacemarksService } from '@src/app/modules/map';
import { OrganisationService } from '@src/core/services';
import { fixRouteParam } from '@src/utils';

import {
  LoyaltyProgramFilterService,
  LoyaltyProgramReadService,
  LpUserProfileService,
  ResultDialogService,
} from './services';
import { ProductDialogComponent } from './product-dialog';
import { LoyaltyProgramFilterData, MapBoundsType, SelectCity } from './types';
import { HeaderComponent } from './components';
import { LpCarouselCategoriesComponent } from './lp-carousel-categories/lp-carousel-categories.component';
import { getBalloonContentByProductList } from './utils';

@Component({
  selector: 'app-loyalty-program',
  templateUrl: './loyalty-program.component.html',
  styleUrls: ['./loyalty-program.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoyaltyProgramComponent extends BaseResizableComponent implements AfterViewInit {
  private readonly resultDialogService = inject(ResultDialogService);
  private readonly dialogService = inject(TuiDialogService);
  private readonly injector = inject(Injector);
  private readonly loyaltyProgramReadService = inject(LoyaltyProgramReadService);
  private readonly loyaltyProgramFilterService = inject(LoyaltyProgramFilterService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly placemarksService = inject(PlacemarksService);
  private readonly organisationService = inject(OrganisationService);
  private readonly lpUserProfileService = inject(LpUserProfileService);
  private readonly ngxPermissionService = inject(NgxPermissionsService);

  @ViewChild(TuiScrollbar, { read: ElementRef }) private scrollBar?: ElementRef<HTMLElement>;

  // HACK: для скрытия\отображения компонента lp-header
  @ViewChild(HeaderComponent, { read: ElementRef }) headerElement?: ElementRef<HTMLElement>;
  headerHeight = 0;
  headerVisible = true;

  // HACK: для вычисления высоты карты
  @ViewChild(LpCarouselCategoriesComponent, { read: ElementRef }) carouselCategoriesElement?: ElementRef<HTMLElement>;
  mapHeight = 0;
  mapWidth = 0;

  private previousScrollPosition = 0;

  readonly products$ = this.loyaltyProgramReadService.products$;
  readonly filter$ = new BehaviorSubject<LoyaltyProgramFilterData>({});
  readonly search$ = new BehaviorSubject<string>('');
  readonly mapProducts$ = this.loyaltyProgramReadService.mapProducts$;
  readonly productsFromFavoriteCategories$ = this.loyaltyProgramReadService.productsFromFavoriteCategories$;

  city?: SelectCity;
  filter: LoyaltyProgramFilterData = {};
  bounds?: MapBoundsType;
  placemarksForMap: PlacemarkType[] = [];

  hasFilter = true;
  loadingProducts = true;
  hasMoreProducts = true;
  loadingProductsFromFavoriteCategories = false;

  productListMode: ListModeType = 'grid';
  listModeStorageKey = StorageKeys.LpProductListMode;

  get search() {
    return this.search$.value;
  }

  constructor() {
    super();

    this.filter$.pipe(takeUntil(this.destroyed$$), debounceTime(500), throttleTime(500)).subscribe(filter => {
      this.hasFilter = this.hasFilterData(filter);

      if (this.hasFilter) {
        if (this.productListMode === 'map') {
          if (this.city && this.bounds) {
            this.loyaltyProgramReadService.getMapProducts({
              city: this.city,
              bounds: this.bounds,
              query: this.search,
              categories: this.filter.categories,
            });
          }
        } else {
          this.loyaltyProgramReadService.getProductsByFilter(filter, this.city, this.search, false);
        }
      }

      this.cdr.markForCheck();
    });
  }

  get showProductsFromFavoriteCategories() {
    return (
      !this.search && !this.filter.categories?.length && !!this.lpUserProfileService.favoriteCategories$.value?.length
    );
  }

  get productsListLoading() {
    return (
      this.loadingProducts || (this.showProductsFromFavoriteCategories && this.loadingProductsFromFavoriteCategories)
    );
  }

  ngAfterViewInit() {
    // HACK: для скрытия\отображения компонента lp-header
    this.headerHeight = this.headerElement?.nativeElement.getBoundingClientRect().height || 0;

    this.getMapSize();

    this.cdr.markForCheck();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    combineLatest([this.lpUserProfileService.userCityId$, this.lpUserProfileService.favoriteCategories$])
      .pipe(takeUntil(this.destroyed$$), debounceTime(300))
      .subscribe(([userCityId, favoriteCategories]) => {
        if (userCityId && favoriteCategories) {
          this.loyaltyProgramReadService.getProductsFromFavoriteCategories(userCityId, favoriteCategories);
        }
      });

    this.route.queryParamMap.pipe(takeUntil(this.destroyed$$)).subscribe(queryParams => {
      const search = fixRouteParam(queryParams.get('search'));

      if (search) {
        this.onSearchChange(search);
      }

      this.cdr.markForCheck();
    });

    this.loyaltyProgramFilterService.getCategories(true);

    this.refresh();

    this.products$.pipe(takeUntil(this.destroyed$$)).subscribe(({ items, hasMore }) => {
      this.loadingProducts = !items;
      this.hasMoreProducts = hasMore;
      this.cdr.markForCheck();
    });

    this.productsFromFavoriteCategories$.pipe(takeUntil(this.destroyed$$)).subscribe(items => {
      this.loadingProductsFromFavoriteCategories = !items;
      this.cdr.markForCheck();
    });

    this.mapProducts$.pipe(takeUntil(this.destroyed$$)).subscribe(items => {
      this.placemarksForMap = (
        items
          ?.map(item => {
            const unionId =
              item.unionId && this.organisationService.currentParentOrganisation$.value?.id !== item.unionId
                ? item.unionId
                : undefined;
            return getBalloonContentByProductList(item, unionId);
          })
          .reduce((acc, val) => acc.concat(val), []) ?? []
      )?.map(item => this.placemarksService.getPlacemarks(item));

      this.cdr.markForCheck();
    });

    this.search$.pipe(takeUntil(this.destroyed$$), debounceTime(500)).subscribe(search => {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { search: search || null },
        queryParamsHandling: 'merge',
      });
    });
  }

  addProduct() {
    this.dialogService
      .open<void | string>(new PolymorpheusComponent(ProductDialogComponent, this.injector), {
        dismissible: false,
        closeable: false,
      })
      .subscribe(id => {
        if (id) {
          this.resultDialogService.open({ type: 'added' }).subscribe({
            complete: () => {
              this.router.navigate(['/loyalty', 'product', id]);
            },
          });
        }
      });
  }

  hasFilterData(filter: LoyaltyProgramFilterData): boolean {
    return this.search.length > 0 || !!filter.categories?.length || !!this.city;
  }

  refresh() {
    this.filter$.next(this.filter);
  }

  onSearchChange(search: string) {
    this.search$.next(search);
    this.refresh();
  }

  onChangeCategory(filter: LoyaltyProgramFilterData, changeRoute: boolean) {
    this.filter = filter;
    this.refresh();

    if (changeRoute) {
      const queryParams = {
        categoryIds: filter.categories?.map(category => category.id),
      };

      this.router.navigate([], { queryParams });
    }
  }

  onScrollProducts() {
    if (this.hasFilterData(this.filter) && this.loyaltyProgramReadService.isProductsByFilterHasMore) {
      this.hasMoreProducts = true;
      this.loyaltyProgramReadService.getProductsByFilter(this.filter, this.city, this.search, true);
    }

    this.cdr.markForCheck();
  }

  onScroll() {
    if (!this.scrollBar) {
      return;
    }

    const { nativeElement } = this.scrollBar;
    const currentScrollPosition = nativeElement.scrollTop;

    if (currentScrollPosition <= this.headerHeight) {
      this.headerVisible = true;
    } else if (currentScrollPosition > this.previousScrollPosition) {
      this.headerVisible = false;
    } else {
      this.headerVisible = true;
    }

    this.previousScrollPosition = currentScrollPosition;

    this.cdr.markForCheck();
  }

  private getMapSize() {
    this.getMapHeight();
    this.getMapWidth();
  }

  private getMapHeight() {
    const permissions = this.ngxPermissionService.getPermissions();

    const carouselCategoriesElement = this.carouselCategoriesElement?.nativeElement;
    if (carouselCategoriesElement) {
      const carouselCategoriesHeight = carouselCategoriesElement.getBoundingClientRect().height || 0;
      const carouselCategoriesComputedStyle = window.getComputedStyle(carouselCategoriesElement);

      let mapPaddingBottom = 0; // INFO: 0px нижний отступ
      // HACK: вычисление высоты карты
      if (this.isDesktop) {
        mapPaddingBottom = 20; // INFO: 20px нижний отступ
      } else {
        mapPaddingBottom = 'showOnlyLoyaltyProgram' in permissions ? 0 : 56; // INFO: 56px высота меню
      }

      this.mapHeight =
        window.innerHeight -
        this.headerHeight -
        carouselCategoriesHeight -
        parseFloat(carouselCategoriesComputedStyle.paddingTop) -
        parseFloat(carouselCategoriesComputedStyle.paddingBottom) -
        parseFloat(carouselCategoriesComputedStyle.marginTop) -
        parseFloat(carouselCategoriesComputedStyle.marginBottom) -
        mapPaddingBottom;
    }

    this.cdr.markForCheck();
  }

  private getMapWidth() {
    const carouselCategoriesElement = this.carouselCategoriesElement?.nativeElement;
    if (carouselCategoriesElement) {
      const carouselCategoriesWidth = carouselCategoriesElement.getBoundingClientRect().width || 0;

      let mapContainerGap = 0; // INFO: 0px gap контейнера, в котором находится список объявлений и map
      let productCardWidth = 0; // INFO: 0px ширина карточки объявления
      let mapContainerStylesWidth = 0; // INFO: 0px ширина стилей контейнера, в котором находится список объявлений и map
      // HACK: вычисление ширины карты
      if (this.isDesktop) {
        mapContainerGap = 20; // INFO: 20px gap контейнера
        productCardWidth = 330; // INFO: 330px ширина карточки объявления
        mapContainerStylesWidth = 40; // INFO: 40px отступы контейнера
      } else {
        mapContainerGap = 0; // INFO: 0px gap контейнера (т.к. списка объявлений нет)
        productCardWidth = 0; // INFO: 0px ширина карточки объявления (т.к. списка объявлений нет)
        mapContainerStylesWidth = 0; // INFO: 0px не учитываем ширину отступов контейнера
      }

      this.mapWidth = carouselCategoriesWidth - mapContainerStylesWidth - mapContainerGap - productCardWidth;
    }

    this.cdr.markForCheck();
  }
}
